/**
 * @file RoundedSwitch.component.tsx
 * @module app/components/RoundedSwitch.component
 * @description Rounded toggle switch UI component.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useState } from "react";

export interface PillItem {
  title: string;
  enable: boolean;
  disabled?: boolean;
}

export interface SegmentedControlProps {
  items: PillItem[];
  // Lets a parent programmatically select a pill (e.g. navigating to a tab from elsewhere in the UI).
  activeTitle?: string;
  onToggle?: (item: PillItem, index: number) => void;
}

export const RoundedSwitch: React.FC<SegmentedControlProps> = ({
  items = [],
  activeTitle,
  onToggle,
}) => {
  const [pills, setPills] = useState<PillItem[]>(items);

  // `items` is re-created on every parent render, so re-sync only when a
  // title's `disabled` state actually changes, keeping the current selection intact.
  const itemsSignature = items
    .map((item) => `${item.title}:${item.disabled ? 1 : 0}`)
    .join("|");

  useEffect(() => {
    setPills((prev) =>
      items.map((item) => {
        const existingPill = prev.find((p) => p.title === item.title);
        return {
          ...item,
          enable: existingPill ? existingPill.enable : item.enable,
        };
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsSignature]);

  useEffect(() => {
    if (!activeTitle) return;
    setPills((prev) =>
      prev.map((pill) => ({ ...pill, enable: pill.title === activeTitle })),
    );
  }, [activeTitle]);

  const handleSelect = (index: number): void => {
    if (pills[index]?.disabled) return;

    const updatedPills = pills.map((pill, i) => ({
      ...pill,
      enable: i === index ? true : pill.disabled ? false : false,
    }));

    setPills(updatedPills);

    if (onToggle) {
      onToggle(updatedPills[index], index);
    }
  };

  return (
    <div
      className="d-inline-flex align-items-center p-3 bg-body-tertiary border border-secondary-subtle rounded-pill shadow-sm gap-2"
      style={{
        backdropFilter: "blur(8px)",
      }}
    >
      {pills.map((pill, index) => (
        <button
          key={`${pill.title}-${index}`}
          type="button"
          onClick={() => handleSelect(index)}
          disabled={pill.disabled}
          aria-disabled={pill.disabled}
          className={`btn btn-lg px-5 py-3 rounded-pill fw-semibold text-capitalize position-relative ${
            pill.disabled
              ? "bg-secondary-subtle text-secondary border border-secondary-subtle"
              : pill.enable
              ? "bg-white text-dark shadow-sm border border-secondary-subtle"
              : "bg-light text-dark-emphasis border border-secondary-subtle opacity-100"
          }`}
          style={{
            transition: "all 0.2s ease-in-out",
            fontSize: "1.05rem",
            letterSpacing: "0.01em",
            cursor: pill.disabled ? "not-allowed" : "pointer",
          }}
        >
          {pill.title}
        </button>
      ))}
    </div>
  );
};
